// src/services/sectionService.js
import { api } from './api';
import { objectToQueryString } from '../utils/apiUtils';

/**
 * Get all sections for a project
 * @param {string} projectId - Project ID
 * @returns {Promise} - Promise with response
 */
export const getProjectSections = async (projectId) => {
  try {
    const response = await api.get(`/projects/${projectId}/sections`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Get a specific section by ID
 * @param {string} sectionId - Section ID
 * @returns {Promise} - Promise with response
 */
export const getSection = async (sectionId) => {
  try {
    const response = await api.get(`/sections/${sectionId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Create a new section
 * @param {string} projectId - Project ID
 * @param {Object} sectionData - Section data
 * @returns {Promise} - Promise with response
 */
export const createSection = async (projectId, sectionData) => {
  try {
    const response = await api.post(`/projects/${projectId}/sections`, sectionData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Update a section
 * @param {string} sectionId - Section ID
 * @param {Object} sectionData - Updated section data
 * @returns {Promise} - Promise with response
 */
export const updateSection = async (sectionId, sectionData) => {
  try {
    const response = await api.put(`/sections/${sectionId}`, sectionData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Delete a section
 * @param {string} sectionId - Section ID
 * @returns {Promise} - Promise with response
 */
export const deleteSection = async (sectionId) => {
  try {
    const response = await api.delete(`/sections/${sectionId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Reorder sections within a project
 * @param {string} projectId - Project ID
 * @param {Array} sectionIds - Array of section IDs in desired order
 * @returns {Promise} - Promise with response
 */
export const reorderSections = async (projectId, sectionIds) => {
  try {
    const response = await api.post(`/projects/${projectId}/sections/reorder`, {sectionIds} );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Set section collapsed state
 * @param {string} sectionId - Section ID
 * @param {boolean} collapsed - Whether section should be collapsed
 * @returns {Promise} - Promise with response
 */
export const setSectionCollapsed = async (sectionId, collapsed) => {
  try {
    const response = await api.patch(`/sections/${sectionId}/collapsed`, { collapsed });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Get tasks in a section
 * @param {string} sectionId - Section ID
 * @param {Object} params - Query parameters
 * @returns {Promise} - Promise with response
 */
export const getSectionTasks = async (sectionId, params = {}) => {
  try {
    const response = await api.get(`/sections/${sectionId}/tasks`, { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Add tasks to a section
 * @param {string} sectionId - Section ID
 * @param {Array} taskIds - Array of task IDs to add to section
 * @returns {Promise} - Promise with response
 */
export const addTasksToSection = async (sectionId, taskIds) => {
  try {
    const response = await api.post(`/sections/${sectionId}/tasks`, { taskIds });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Remove tasks from a section
 * @param {string} sectionId - Section ID
 * @param {Array} taskIds - Array of task IDs to remove
 * @returns {Promise} - Promise with response
 */
export const removeTasksFromSection = async (sectionId, taskIds) => {
  try {
    const response = await api.delete(`/sections/${sectionId}/tasks`, { 
      data: { taskIds } 
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Get all sections with tasks (optimized for Kanban/List views)
 * @param {string} projectId - Project ID
 * @param {Object} params - Query parameters
 * @returns {Promise} - Promise with sections and tasks
 */
export const getProjectSectionsWithTasks = async (projectId, params = {}) => {
  try {
    const queryString = objectToQueryString(params);
    const response = await api.get(`/projects/${projectId}/sections/with-tasks${queryString}`);
    // The api.get already returns the parsed JSON, not wrapped in { data: ... }
    return response;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Load more tasks for a specific section
 * @param {string} sectionId - Section ID
 * @param {Object} params - Query parameters (page, limit, showCompleted)
 * @returns {Promise} - Promise with tasks and pagination info
 */
export const loadMoreSectionTasks = async (sectionId, params = {}) => {
  try {
    const queryString = objectToQueryString(params);
    const response = await api.get(`/sections/${sectionId}/tasks/more${queryString}`);
    // The api.get already returns the parsed JSON, not wrapped in { data: ... }
    return response;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Load more unsectioned tasks for a project
 * @param {string} projectId - Project ID
 * @param {Object} params - Query parameters (page, limit, showCompleted)
 * @returns {Promise} - Promise with tasks and pagination info
 */
export const loadMoreUnsectionedTasks = async (projectId, params = {}) => {
  try {
    const queryString = objectToQueryString(params);
    const response = await api.get(`/projects/${projectId}/sections/unsectioned-tasks/more${queryString}`);
    // The api.get already returns the parsed JSON, not wrapped in { data: ... }
    return response;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export default {
  getProjectSections,
  getSection,
  createSection,
  updateSection,
  deleteSection,
  reorderSections,
  setSectionCollapsed,
  getSectionTasks,
  addTasksToSection,
  removeTasksFromSection,
  getProjectSectionsWithTasks,
  loadMoreSectionTasks,
  loadMoreUnsectionedTasks
};